import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@invent/wl-ui-kit-next/ui';
import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

import { TEAM_PATHS } from '@/domains/admin';

import type { Provenance } from './fixtures';
import { Annotation, ProvenanceBadge } from './shared';

/**
 * The two panels that are not the respondent's screens: what staff see while the registry
 * providers are stubs, and what the backend still owes.
 *
 * The second one is the point of the whole study. A prototype whose gaps are only in the
 * tooltips reads as a finished product to everybody who did not build it.
 *
 * The first now has a live counterpart, `/team/registrations`, and links to it the way a
 * `live` screen's strip does — one way, in a new tab. It draws what that screen draws and no
 * more: a row carries no action there (verifying a number is the participant card's), so it
 * carries none here. The link is a literal path rather than a constant: the admin section
 * exports none, and its rail and routes spell theirs the same way. Following it needs a staff
 * or admin session; the study itself deliberately needs none.
 */

const LIVE_QUEUE_ROUTE = TEAM_PATHS.registrations;

interface IQueueRow {
  startedAt: string;
  lastActivityAt: string;
  survey: string;
  step: string;
  status: 'in_progress' | 'completed' | 'abandoned';
  resolution?: string;
  refusal?: string;
  participant?: string;
  unverified?: boolean;
  /** The name the registry returned for the run's number — `registryName` (SR-215). */
  registryName?: string;
  what: string;
}

/** Invented rows in the shape `GET /api/v1/admin/registrations` actually answers. */
const QUEUE: IQueueRow[] = [
  {
    startedAt: '19 Sep, 09:12',
    lastActivityAt: '19 Sep, 09:12',
    survey: 'FA',
    step: 'anchor',
    status: 'abandoned',
    resolution: 'number_taken',
    participant: 'Curtis Bellweather',
    registryName: 'CURTIS JAMES BELLWEATHER',
    what: 'CRD 4418872 is already an account’s. The registrant was told only the value.'
  },
  {
    startedAt: '19 Sep, 08:44',
    lastActivityAt: '19 Sep, 08:45',
    survey: 'FA',
    step: 'confirm_identity',
    status: 'in_progress',
    what: 'Walked away at “is this you?”. This is the stuck half — no finishedAt, and the ' +
      'newest step is 40 minutes old. Seven days of that and the sweep ends it as abandoned.'
  },
  {
    startedAt: '19 Sep, 08:02',
    lastActivityAt: '19 Sep, 08:29',
    survey: 'RIA_FIRM',
    step: 'complete',
    status: 'in_progress',
    refusal: 'address_belongs_to_another_account',
    what: 'One of the 54. Passed every screen, refused at the last, and cannot recover a ' +
      'password either. The way out is on their participant’s card, where staff link the ' +
      'account they should sign in with — this row only says they tried.'
  },
  {
    startedAt: '18 Sep, 17:20',
    lastActivityAt: '18 Sep, 17:23',
    survey: 'FSP',
    step: 'complete',
    status: 'completed',
    participant: 'Alicia Moreno',
    unverified: true,
    what: 'Landed. NIPR was not asked — the provider is a stub — so the NPN carries no ' +
      'verified_at and somebody has to read it against the registry by hand.'
  }
];

export function StaffQueuePanel() {
  return (
    <div className="mx-auto grid max-w-5xl gap-4 px-6 py-8">
      <header className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg text-[var(--color-royal-blue)]">Registration attempts</h2>
        <ProvenanceBadge provenance="shipped" />
        <p className="text-sm text-muted-foreground">
          <code>GET /api/v1/admin/registrations</code> — staff and admin, under{' '}
          <code>responses.browse</code>.
        </p>
        <Link
          to={LIVE_QUEUE_ROUTE}
          target="_blank"
          rel="noreferrer"
          className="ml-auto inline-flex items-center gap-1 text-sm text-[var(--color-sapphire)] underline underline-offset-2"
        >
          The real screen: <span className="font-mono">{LIVE_QUEUE_ROUTE}</span>
          <ExternalLink className="size-3" aria-hidden />
          <span className="sr-only">(opens in a new tab)</span>
        </Link>
      </header>

      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Started</TableHead>
              <TableHead>Survey</TableHead>
              <TableHead>Step</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Registry’s name</TableHead>
              <TableHead>Participant</TableHead>
              <TableHead>Last activity</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {QUEUE.map((row) => (
              <TableRow key={row.startedAt}>
                <TableCell className="whitespace-nowrap text-xs">{row.startedAt}</TableCell>
                <TableCell className="font-mono text-xs">{row.survey}</TableCell>
                <TableCell className="font-mono text-xs">{row.step}</TableCell>
                <TableCell className="text-xs">
                  <span className="grid gap-1">
                    <Badge variant="outline">{row.status}</Badge>
                    {row.resolution && (
                      <span className="font-mono text-[11px] text-[var(--color-deep-magenta)]">
                        {row.resolution}
                      </span>
                    )}
                    {row.refusal && (
                      <span className="font-mono text-[11px] text-[var(--color-deep-magenta)]">
                        {row.refusal}
                      </span>
                    )}
                  </span>
                </TableCell>
                <TableCell className="text-xs">
                  {row.registryName ?? <span className="text-muted-foreground">—</span>}
                </TableCell>
                <TableCell className="text-xs">
                  {row.participant ? (
                    <span className="flex flex-wrap items-center gap-1">
                      {row.participant}
                      {row.unverified && (
                        <Badge variant="outline" className="border-amber-300 text-amber-900">
                          unverified
                        </Badge>
                      )}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="whitespace-nowrap text-xs">{row.lastActivityAt}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <ul className="grid gap-2 text-xs text-muted-foreground">
        {QUEUE.map((row) => (
          <li key={`note-${row.startedAt}`}>
            <span className="font-mono text-[var(--color-royal-blue)]">{row.startedAt}</span> —{' '}
            {row.what}
          </li>
        ))}
      </ul>

      <div className="grid gap-2">
        <Annotation title="Reading a refusal takes two members, not one">
          A number already taken ends the run: <code>status: abandoned</code> with{' '}
          <code>resolution: number_taken</code>, and <code>participant</code> names who holds
          it — nothing of which ever reached the registrant. An address refusal leaves the run{' '}
          <code>in_progress</code>, because an address is something the registrant can change.
        </Annotation>
        <Annotation title="There is no default filter, deliberately">
          <code>?status=</code> is repeatable and nothing is hidden from a reader who did not
          know to ask. “Stuck and refused” is therefore{' '}
          <code>?status=in_progress&amp;status=abandoned</code>, which is a choice the caller
          makes rather than one the list makes for them.
        </Annotation>
        <Annotation title="Verifying a number is the participant card’s, not the queue’s">
          A row naming a participant links to their card, where “Verify number” sits behind a
          confirmation — one place an irreversible stamp is pressed from.{' '}
          <code>POST /admin/participants/&#123;id&#125;/identifier-verifications</code> names
          the number, <code>{'{"idType":"CRD","value":"…"}'}</code>, because staff read the
          registry in another tab and the value is what they have in front of them. It only
          ever sets: a row already carrying a <code>verified_at</code> is answered with what it
          has, so re-stamping cannot rewrite when a confirmation happened or who made it.
        </Annotation>
        <Annotation title="Why this screen is load-bearing rather than a nicety" tone="gap">
          NIPR is a stub that answers <code>error</code>, so every FSP registration lands
          unverified, and an FA or RIA one does whenever FINRA cannot be asked, the SEC ADV
          register is empty or stale, or the registry names nobody — this queue is the only
          place those outcomes are visible. It is also where one of the 54 who tried shows up;
          the fix is on the person hub, which links an account to them (block 6), and the
          People list’s “No account” filter lists every one of them, tried or not.
        </Annotation>
      </div>
    </div>
  );
}

interface IGap {
  track: string;
  what: string;
  /** What the prototype does in its place. */
  prototype: string;
  /** What has to exist for the prototype's behaviour to be real. */
  owed: string;
  provenance: Provenance;
}

const GAPS: IGap[] = [
  {
    track: 'C1 · BrokerCheck',
    what:
      'A real CRD lookup, shipped in block 3: asked in the anchor request, and again through ' +
      'the job queue at completion, where its answer becomes an external_fact.',
    prototype:
      'An invented CRD directory, answering ok or not_found in both switch positions — the ' +
      'product asks the real one, so the stubbed position does too.',
    owed:
      'Nothing for registration: a team’s route step now carries the firm — the register firms ' +
      'FINRA’s firm maps to, or FINRA’s name — since block 7. A team registered while FINRA ' +
      'could not be asked stores no firm, so answering again later starts at trust company or ' +
      'RIA (B5).',
    provenance: 'shipped'
  },
  {
    track: 'C2 · NIPR',
    what: 'FSP’s anchor. The contract has not been read at all.',
    prototype: 'An invented NPN ⇄ licence pairing, as FSP-AC-001 describes it.',
    owed:
      'Read the contract first. It anchors an entire survey and is the largest unknown on the ' +
      'roadmap.',
    provenance: 'stubbed'
  },
  {
    track: 'C3 · SEC ADV',
    what:
      'The firm legal name the RIA registration confirms, from ADV Item 1.A — shipped in ' +
      'block 4, read from a register the worker imports from the SEC’s daily feed. Registered ' +
      'advisers only: an exempt reporting adviser answers not_found like an unknown number.',
    prototype:
      'An invented SEC-number directory, with one number that matches nothing, and the ' +
      'server’s one shape for an SEC number mirrored — the anchor refuses any other.',
    owed:
      'Nothing for registration. What an ERA or a platform RIA’s member practice should be ' +
      'offered is open (survey-api’s roadmap Q7), and a redirected firm lands with no survey.',
    provenance: 'shipped'
  },
  {
    track: 'C4 · The firm register',
    what:
      'The firms a team is routed by — shipped in block 7: forty firms seeded by survey-api’s ' +
      'migration, behind firm CRDs, and owned by staff from then on (/team/setup/team-routing, ' +
      'one Firms table by CRD), where a firm CRD seen at registration that no register name holds ' +
      'is under Not matched yet. The wirehouse list routes; the register names.',
    prototype:
      'Seven of the seeded forty, copied as seeded, behind the invented leads’ FINRA firms.',
    owed: 'Nothing: the register is data, and staff keep it.',
    provenance: 'shipped'
  },
  {
    track: 'D1 · Team registration and routing',
    what:
      'TEAM-RG-001, TEAM-RG-004…006: a team registers on its lead’s CRD, routed by SR-416’s ' +
      'questions, entered by whether its firm is on the wirehouse list — Wires, Independent ' +
      'Practices, or the RIA firm survey as a new run. Shipped in block 7 and moved to SR-416’s ' +
      'tree, with the routing a team answers for itself and staff’s decision over it.',
    prototype:
      'The live flow, mirrored: the route step, “no CRD”, the redirect, and survey-api’s tree ' +
      'evaluated here in its place.',
    owed:
      'An org holding a lead CRD and no response or decision is no team to the routing read ' +
      '(B2), and staff see a routing’s answers as codes, since no route serves the questions ' +
      '(B4).',
    provenance: 'shipped'
  },
  {
    track: 'D2 · First login for a migrated account',
    what:
      'The one-time “your CRD is X, confirm it”, shipped in block 3: GET /my/identifiers and ' +
      'POST /my/identifier-confirmations.',
    prototype:
      'The confirmation screen, for both migrated populations, in the words of the product’s ' +
      'card — which asks it on the catalogue instead, since survey-api ruled the question ' +
      'gates nothing.',
    owed:
      'Nothing for migrated accounts — the ruling is in: the answer writes self_confirmation ' +
      'and never verified_at (Q4), and a denial clears nothing. One ask is open: registration ' +
      'leaves self_confirmation null, so a registrant who said yes to the prompt is asked the ' +
      'same on the catalogue — the card shows, as the owner ruled, until survey-api records ' +
      'the first answer.',
    provenance: 'shipped'
  },
  {
    track: 'B1/B3 · Mail and the activation campaign',
    what:
      '45 336 accounts have no password; since block 5 they can ask for a reset link, but ' +
      'nothing tells them to.',
    prototype: 'The message that would reach them, and what happens when they open it.',
    owed:
      'Block 5 shipped the transport — MailJet from the worker, templates, a per-account ' +
      'reset limit, the test-environment allowlist. Owed: bounce and complaint handling, then ' +
      'a campaign with its own token lifetime and a way to see who has landed.',
    provenance: 'not-built'
  },
  {
    track: 'A support channel',
    what:
      'Two screens tell a refused registrant to contact the Veritant team — a taken number, and ' +
      'an address the completion refuses. Nothing says how: no address in the app, no ' +
      'contact route on the API, no transport to answer one with.',
    prototype:
      'A contact block carrying the run id as a reference, with the address rendered as a ' +
      'blank rather than as a plausible-looking support@.',
    owed:
      'Client content first: which address, or a form. If it is a form, a route and a place ' +
      'to put what it receives; if it is an address, it still needs somebody reading it, ' +
      'because the three cases behind these screens (a taken number, one of the 54, a stuck ' +
      'run) are all resolved by hand.',
    provenance: 'not-built'
  },
  {
    track: 'F3 · Address collisions',
    what:
      'The 206 migrated people — 54 of them holding responses — whose legacy address went to ' +
      'another login: they cannot register and cannot recover a password. Shipped in block 6.',
    prototype: 'The dead end itself, and the staff row that shows it.',
    owed:
      'Nothing on the product side: staff find them under the search’s “no account” filter ' +
      'and link an account on the participant card — the one that holds their address, or a ' +
      'new seat at another. Merging two participants of one person stays out (a data ' +
      'repair), and nothing tells the refused registrant where to turn (the support channel).',
    provenance: 'shipped'
  },
  {
    track: 'B2 · Address confirmation',
    what:
      'Whether a registrant receives mail where they said, which nothing checked. Shipped in ' +
      'block 6: the completion mails a link, and a mailed reset or enrolment link counts too.',
    prototype:
      'The prompt the product draws under its header, on the landing after a registration and ' +
      'for a migrated account signing in.',
    owed:
      'Nothing — it gates nothing, by survey-api’s ruling. Changing an address, by its owner or ' +
      'by staff, is the follow-up: until it exists a typo is repaired by a new seat and a link.',
    provenance: 'shipped'
  },
  {
    track: 'The catalogue · what is left of it',
    what:
      'Scoped, and a response opens over POST /api/v1/responses — both shipped, and the ' +
      'closed-cycle gate with them. Registration opens none: a completed one entitles its ' +
      'participant, and the respondent starts from the catalogue. The account the scope leaves ' +
      'holding nothing — 10 586 of 55 056 — has “Add a survey”, registration inside the account.',
    prototype:
      'The empty catalogue with the product’s “Add a survey”, and the one answer it lacks — ' +
      '“none of these describes me”.',
    owed:
      'Somewhere for a “none of these” to land. And a firm redirected to the team survey lands ' +
      'on an empty catalogue: a redirected registration entitles nothing.',
    provenance: 'not-built'
  }
];

export function GapsPanel() {
  return (
    <div className="mx-auto grid max-w-5xl gap-4 px-6 py-8">
      <header className="grid gap-1">
        <h2 className="text-lg text-[var(--color-royal-blue)]">
          What this prototype pretends, and what it would take to stop pretending
        </h2>
        <p className="text-sm text-muted-foreground">
          One row per gap between the screens above and the running product. The roadmap track
          is survey-api’s own numbering.
        </p>
      </header>

      <div className="grid gap-2">
        {GAPS.map((gap) => (
          <article key={gap.track} className="rounded-lg border bg-card p-4">
            <header className="flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-medium text-[var(--color-royal-blue)]">{gap.track}</h3>
              <ProvenanceBadge provenance={gap.provenance} />
            </header>
            <p className="mt-1 text-sm text-muted-foreground">{gap.what}</p>
            <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
              <div>
                <dt className="font-medium text-[var(--color-royal-blue)]">In the prototype</dt>
                <dd className="mt-0.5 text-muted-foreground">{gap.prototype}</dd>
              </div>
              <div>
                <dt className="font-medium text-[var(--color-royal-blue)]">Owed by the backend</dt>
                <dd className="mt-0.5 text-muted-foreground">{gap.owed}</dd>
              </div>
            </dl>
          </article>
        ))}
      </div>

      <Annotation title="Three questions this study asked, all settled">
        <ol className="grid list-inside list-decimal gap-1">
          <li>
            Team registration order: the lead advisor’s CRD first, as the Shook note on
            TEAM-AC-003 asks, and the team’s name at the last step.
          </li>
          <li>
            What a FiNet answer means: no such question. The register holds Wells Fargo’s
            independent network as a gating firm of its own, apart from Wells Fargo Advisors.
          </li>
          <li>
            A respondent confirming their own CRD does <strong>not</strong> write the{' '}
            <code>verified_at</code> a staff verification does — survey-api’s Q4, ruled no.
          </li>
        </ol>
      </Annotation>
    </div>
  );
}
